<template>
    <div class="py-12">
        <h1
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ t("vue.changelog") }}
        </h1>

        <div class="mt-4">
            <inertia-link :href="route('kiosk.changelog.create')">
                <AtomicButton size="sm" color="yellow">
                    {{ t("vue.create") }}
                    <template #icon>
                        <PlusCircleIcon
                            class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </inertia-link>
        </div>

        <DataTable class="mt-10" :headers="headers" :items="entries" searchable>
            <template #item-id="{ data }">
                <div class="flex p-6 text-gray-500">
                    {{ data }}
                </div>
            </template>

            <template #item-version="{ data }">
                <div class="flex p-6 font-medium text-gray-800">
                    {{ data }}
                </div>
            </template>

            <template #item-release_date="{ data }">
                <div class="flex p-6 text-gray-500">
                    {{ format(new Date(data as string), "d. L. yyyy") }}
                </div>
            </template>

            <template #item-blocks="{ data }">
                <div class="flex p-6 text-gray-500">
                    {{ (data as unknown[]).length }}
                </div>
            </template>

            <template #item-custom_edit="{ row }">
                <inertia-link
                    :href="
                        route('kiosk.changelog.edit', {
                            changelogEntry: row.id as number,
                        })
                    "
                >
                    <AtomicButton size="sm" color="white">
                        <template #icon>
                            <PencilSquareIcon
                                class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                            />
                        </template>
                    </AtomicButton>
                </inertia-link>
            </template>

            <template #item-custom_delete="{ row }">
                <AtomicButton
                    size="sm"
                    color="red"
                    @click="deleteEntry(row as IChangelogEntry)"
                >
                    <template #icon>
                        <TrashIcon
                            class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import { router } from "@inertiajs/vue3";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import type IChangelogEntry from "@/types/Changelog/IChangelogEntry";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";

defineProps<{
    entries: IPaginationData<IChangelogEntry>;
}>();

const { t } = useTranslations();
const { show } = useConfirm();

const headers: IDataTableHeader[] = [
    { text: t("vue.id"), id: "id", sortable: true, sortableId: "id" },
    {
        text: t("vue.version"),
        id: "version",
        sortable: true,
        sortableId: "version",
    },
    {
        text: t("vue.release_date"),
        id: "release_date",
        sortable: true,
        sortableId: "release_date",
    },
    { text: t("vue.changelog_blocks"), id: "blocks" },
    { text: "", id: "custom_edit" },
    { text: "", id: "custom_delete" },
];

const deleteEntry = async (entry: IChangelogEntry) => {
    const confirmed = await show({
        header: t("vue.delete"),
        message: t("vue.delete_changelog_entry_message"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(
            route("kiosk.changelog.destroy", {
                changelogEntry: entry.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: t("vue.changelog_entry_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: t("vue.changelog_entry_not_deleted"),
                        type: "error",
                    });
                },
            },
        );
    }
};
</script>
